<template>
  <!--
    The hover zone is the whole row: the icon button and the `trigger` slot
    (the field label, right of the icon) together (APP-1450).
  -->
  <div
    ref="rootRef"
    :class="structure['root']"
    @mouseenter="handlePointerEnter"
    @mouseleave="handlePointerLeave"
  >
    <span :class="structure['anchor']">
      <button
        type="button"
        :class="[structure['trigger'], skin['trigger'], interaction['trigger']]"
        :aria-label="label"
        :aria-expanded="visible"
        :aria-describedby="visible ? panelId : undefined"
        :disabled="disabled"
        @click.stop="handleClick"
        @focus="handleFocus"
        @blur="handleBlur"
      >
        <slot name="icon" />
      </button>

      <div
        v-if="visible"
        :class="structure['positioner']"
      >
        <div
          :id="panelId"
          role="tooltip"
          :class="[structure['panel'], skin['panel']]"
        >
          {{ content }}
        </div>
      </div>
    </span>

    <slot name="trigger" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId, useTemplateRef, watch } from 'vue'

import { useDismissable } from '~/utils/useDismissable'

import structure from './Tooltip.structure.module.css'
import skin from './Tooltip.skin.module.css'
import interaction from './Tooltip.interaction.module.css'

const props = withDefaults(
  defineProps<{
    /** Tooltip copy. Callers pass already-translated text. */
    content: string
    /** Accessible name for the icon button. */
    label: string
    /**
     * Which gestures open the panel. `hover` follows the pointer over the
     * whole label-and-icon row; `click` latches open until dismissed. Keyboard
     * focus on the icon button opens the panel in every mode.
     */
    triggerOn?: 'both' | 'click' | 'hover'
    /** Suppresses the panel entirely and disables the icon button. */
    disabled?: boolean
    /** Renders the panel already open, for hints that announce themselves. */
    defaultOpen?: boolean
  }>(),
  { defaultOpen: false, disabled: false, triggerOn: 'both' }
)

const emit = defineEmits<{ open: []; close: [] }>()

defineSlots<{
  /** The label the tooltip describes. Rendered right of the icon, inside the hover zone. */
  trigger?: () => unknown
  /** The help affordance. A slot because atoms cannot render icon packages. */
  icon?: () => unknown
}>()

// A generated id, not join.vue's hardcoded string: two tooltips on one page
// would otherwise share an id and mis-target aria-describedby.
const panelId = useId()

const rootRef = useTemplateRef<HTMLElement>('rootRef')
const clickOpen = ref(false)
const hovered = ref(false)
const focused = ref(false)

const visible = computed(
  () => !props.disabled && (clickOpen.value || hovered.value || focused.value)
)

const opensOnHover = computed(() => props.triggerOn !== 'click')
const opensOnClick = computed(() => props.triggerOn !== 'hover')

const handlePointerEnter = (): void => {
  if (opensOnHover.value) hovered.value = true
}

const handlePointerLeave = (): void => {
  hovered.value = false
}

const handleFocus = (): void => {
  focused.value = true
}

const handleBlur = (): void => {
  focused.value = false
}

const handleClick = (): void => {
  if (!opensOnClick.value || props.disabled) return
  if (clickOpen.value) {
    clickOpen.value = false
    focused.value = false
  } else {
    clickOpen.value = true
  }
}

watch(
  () => props.defaultOpen,
  (isOpen) => {
    clickOpen.value = isOpen
  },
  { immediate: true }
)

// Every open input must be cleared, or Escape appears to do nothing while the
// pointer or focus still holds the panel open.
const close = (): void => {
  clickOpen.value = false
  hovered.value = false
  focused.value = false
}

useDismissable(rootRef, { close, isOpen: visible })

watch(visible, (isVisible) => {
  if (isVisible) emit('open')
  else emit('close')
})

defineExpose({
  close,
  open: (): void => {
    clickOpen.value = true
  }
})
</script>
